import { useState, useEffect, useRef } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import {
  PoundSterling,
  GitBranch,
  Clock,
  ThumbsUp,
  TrendingUp,
  Star,
} from 'lucide-react';
import { GlassCard } from '../ui/GlassCard';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';

// ─── Animated Counter ────────────────────────────────────────────────

function useAnimatedValue(
  target: number,
  duration = 1.5,
  enabled = true
): number {
  const [value, setValue] = useState(0);
  const motionVal = useMotionValue(0);
  const rounded = useTransform(motionVal, (v) => Math.round(v));

  useEffect(() => {
    if (!enabled) return;
    motionVal.set(0);
    const controls = animate(motionVal, target, {
      duration,
      ease: [0.25, 0.46, 0.45, 0.94],
    });
    const unsubscribe = rounded.on('change', (v) => setValue(v));
    controls.play();
    return () => {
      controls.stop();
      unsubscribe();
    };
  }, [target, duration, enabled, motionVal, rounded]);

  return value;
}

// ─── Sparkline Data ──────────────────────────────────────────────────

function generateSparkline(length = 12): { v: number }[] {
  let v = 50;
  return Array.from({ length }, () => {
    v = Math.max(20, Math.min(90, v + (Math.random() - 0.45) * 20));
    return { v: Math.round(v) };
  });
}

// ─── Star Rating ─────────────────────────────────────────────────────

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= Math.floor(rating);
        const partial = !filled && i === Math.ceil(rating);
        const pct = partial ? (rating % 1) * 100 : 0;
        return (
          <div key={i} className="relative">
            <Star
              className="w-4 h-4 text-white/10"
              fill="transparent"
              strokeWidth={1.5}
            />
            {(filled || partial) && (
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: filled ? '100%' : `${pct}%` }}
              >
                <Star
                  className="w-4 h-4 text-warning"
                  fill="currentColor"
                  strokeWidth={1.5}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Card 1: Cost Savings ────────────────────────────────────────────

function CostSavingsCard({ delay }: { delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });
  const animatedValue = useAnimatedValue(24, 1.5, isInView);
  const sparkline = useRef(generateSparkline(16)).current;

  return (
    <GlassCard delay={delay} className="relative" ref={ref}>
      <div className="flex items-start justify-between mb-4">
        <div className="space-y-1">
          <p className="text-sm font-medium text-text-secondary">Cost Savings</p>
          <motion.p
            className="text-3xl font-bold text-text-primary tabular-nums"
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: delay + 0.2, duration: 0.5 }}
          >
            £{animatedValue / 10}M
          </motion.p>
          <p className="text-xs text-text-muted">Saved this quarter</p>
        </div>
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-success/20 to-success/5 flex items-center justify-center text-success">
          <PoundSterling className="w-5 h-5" />
        </div>
      </div>

      {/* Sparkline */}
      <div className="h-10 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={sparkline}>
            <defs>
              <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00d084" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#00d084" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="v"
              stroke="#00d084"
              strokeWidth={1.5}
              fill="url(#costGrad)"
              isAnimationActive={true}
              animationDuration={1500}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Badge */}
      <div className="mt-3 flex items-center gap-2">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/10 text-success text-xs font-medium">
          <TrendingUp className="w-3 h-3" />
          +23% vs last quarter
        </span>
      </div>
    </GlassCard>
  );
}

// ─── Card 2: Deflection Rate ─────────────────────────────────────────

function DeflectionRateCard({ delay }: { delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });
  const animatedValue = useAnimatedValue(785, 1.5, isInView);
  const donutData = [
    { name: 'deflected', value: 78.5 },
    { name: 'escalated', value: 21.5 },
  ];

  return (
    <GlassCard delay={delay} className="relative" ref={ref}>
      <div className="flex items-start justify-between mb-4">
        <div className="space-y-1">
          <p className="text-sm font-medium text-text-secondary">Deflection Rate</p>
          <motion.p
            className="text-3xl font-bold text-text-primary tabular-nums"
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: delay + 0.2, duration: 0.5 }}
          >
            {(animatedValue / 10).toFixed(1)}%
          </motion.p>
          <p className="text-xs text-text-muted">Resolved without human agent</p>
        </div>
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-info/20 to-info/5 flex items-center justify-center text-info">
          <GitBranch className="w-5 h-5" />
        </div>
      </div>

      {/* Donut chart */}
      <div className="h-14 flex items-center justify-center -my-1">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={donutData}
              cx="50%"
              cy="50%"
              innerRadius={16}
              outerRadius={26}
              startAngle={90}
              endAngle={-270}
              dataKey="value"
              stroke="none"
              isAnimationActive={true}
              animationDuration={1200}
              animationBegin={delay * 1000 + 300}
            >
              <Cell fill="#00d084" />
              <Cell fill="rgba(255,255,255,0.08)" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2 flex items-center gap-2">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/10 text-success text-xs font-medium">
          <TrendingUp className="w-3 h-3" />
          +5.2% improvement
        </span>
      </div>
    </GlassCard>
  );
}

// ─── Card 3: Avg Call Duration ───────────────────────────────────────

function CallDurationCard({ delay }: { delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  return (
    <GlassCard delay={delay} className="relative" ref={ref}>
      <div className="flex items-start justify-between mb-4">
        <div className="space-y-1">
          <p className="text-sm font-medium text-text-secondary">Avg Call Duration</p>
          <motion.p
            className="text-3xl font-bold text-text-primary tabular-nums"
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: delay + 0.2, duration: 0.5 }}
          >
            2:24
          </motion.p>
          <p className="text-xs text-text-muted">Average AI-handled call</p>
        </div>
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-warning/20 to-warning/5 flex items-center justify-center text-warning">
          <Clock className="w-5 h-5" />
        </div>
      </div>

      {/* Comparison bar */}
      <div className="space-y-2 mt-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-text-muted">AI Agent</span>
          <span className="text-text-primary font-medium">2:24</span>
        </div>
        <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-success"
            initial={{ width: 0 }}
            animate={isInView ? { width: '29%' } : {}}
            transition={{ delay: delay + 0.4, duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-text-muted">Human Agent</span>
          <span className="text-text-primary font-medium">8:15</span>
        </div>
        <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-white/20"
            initial={{ width: 0 }}
            animate={isInView ? { width: '100%' } : {}}
            transition={{ delay: delay + 0.5, duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          />
        </div>
      </div>

      <div className="mt-3">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/10 text-success text-xs font-medium">
          71% faster
        </span>
      </div>
    </GlassCard>
  );
}

// ─── Card 4: Customer Satisfaction ───────────────────────────────────

function SatisfactionCard({ delay }: { delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });
  const animatedValue = useAnimatedValue(46, 1.5, isInView);

  return (
    <GlassCard delay={delay} className="relative" ref={ref}>
      <div className="flex items-start justify-between mb-4">
        <div className="space-y-1">
          <p className="text-sm font-medium text-text-secondary">Customer Satisfaction</p>
          <motion.p
            className="text-3xl font-bold text-text-primary tabular-nums"
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: delay + 0.2, duration: 0.5 }}
          >
            {(animatedValue / 10).toFixed(1)}/5
          </motion.p>
          <p className="text-xs text-text-muted">AI call satisfaction</p>
        </div>
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-vokentra/20 to-vokentra/5 flex items-center justify-center text-vokentra">
          <ThumbsUp className="w-5 h-5" />
        </div>
      </div>

      {/* Star rating */}
      <div className="flex items-center gap-2 mt-2">
        <StarRating rating={4.6} />
        <span className="text-xs text-text-muted">4.6</span>
      </div>

      {/* Satisfaction bar */}
      <div className="mt-3 space-y-1.5">
        {[
          { label: '5 star', pct: 62, color: 'bg-success' },
          { label: '4 star', pct: 24, color: 'bg-success/70' },
          { label: '3 star', pct: 10, color: 'bg-warning/70' },
          { label: '2 star', pct: 3, color: 'bg-warning' },
          { label: '1 star', pct: 1, color: 'bg-error' },
        ].map((row) => (
          <div key={row.label} className="flex items-center gap-2">
            <span className="text-[10px] text-text-muted w-7 text-right">{row.label}</span>
            <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${row.color}`}
                initial={{ width: 0 }}
                animate={isInView ? { width: `${row.pct}%` } : {}}
                transition={{ delay: delay + 0.3 + Math.random() * 0.3, duration: 0.6 }}
              />
            </div>
            <span className="text-[10px] text-text-muted w-5">{row.pct}%</span>
          </div>
        ))}
      </div>

      <div className="mt-3">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/10 text-success text-xs font-medium">
          <TrendingUp className="w-3 h-3" />
          +0.3 vs last month
        </span>
      </div>
    </GlassCard>
  );
}

// ─── Exported Component ──────────────────────────────────────────────

export function KpiSummaryCards() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <CostSavingsCard delay={0} />
      <DeflectionRateCard delay={0.1} />
      <CallDurationCard delay={0.2} />
      <SatisfactionCard delay={0.3} />
    </div>
  );
}

export default KpiSummaryCards;
